import React, {memo, useCallback, useMemo, useState} from 'react';

import {Typography} from '@mui/material';
import {Box} from '@mui/system';
import {Trans, useTranslation} from 'react-i18next';

import logError from '@/utils/errors/logError';

import './styles';

export interface PaymentLinkV2HeaderProps {
  name: string;
  gif?: string;
  image?: string;
  fundraiserType?: string;
  note?: string;
  amount?: number;
  paymentLinkId?: string;
  isCampaign?: boolean;
  isSavingsGoal?: boolean;
  goalName?: string;
}

const PaymentLinkV2Header: React.FC<PaymentLinkV2HeaderProps> = memo(
  ({
    name,
    gif,
    image,
    fundraiserType,
    note,
    amount,
    paymentLinkId,
    isCampaign,
    isSavingsGoal,
    goalName,
  }) => {
    const {t} = useTranslation();
    const [imgError, setImgError] = useState(false);

    const bannerSrc = useMemo(() => image || gif, [image, gif]);

    const handleImgError = useCallback(() => {
      setImgError(true);
      const error = new Error('Banner image failed to load');
      logError(error, {
        fileName: 'PaymentLinkV2Header.tsx',
        functionName: 'handleImgError',
        customTitle: 'Payment Link V2 error loading banner',
        extraTags: {
          payment_link_id: paymentLinkId,
          img_src: bannerSrc,
        },
      });
    }, [bannerSrc, paymentLinkId]);

    const showImage = useMemo(
      () => Boolean(bannerSrc) && !imgError,
      [bannerSrc, imgError],
    );

    const pillText = isSavingsGoal ? goalName : fundraiserType;

    const noteContent = useMemo(() => {
      if (isCampaign) return note;
      if (isSavingsGoal) {
        return note
          ? t('payment_link.note_wrapped', {note})
          : t('payment_link_v2.goal_default_motivation', {name});
      }
      return note ? t('payment_link.note_wrapped', {note}) : '';
    }, [isCampaign, isSavingsGoal, note, name, t]);

    return (
      <Box className="payment-link-v2-header">
        {showImage && (
          <Box className="payment-link-v2-header__banner">
            <img
              src={bannerSrc}
              alt="payment-link-banner"
              className="payment-link-v2-header__banner-img"
              onError={handleImgError}
            />
          </Box>
        )}
        <Box
          className={`payment-link-v2-header__content${
            isSavingsGoal ? ' payment-link-v2-header__content--savings' : ''
          }`}>
          {pillText && (
            <Box className="payment-link-v2-header__pill">
              <Typography
                component="span"
                className="payment-link-v2-header__pill-text">
                {pillText}
              </Typography>
            </Box>
          )}
          {name && (
            <Typography
              component="p"
              className="payment-link-v2-header__title">
              {isCampaign ? (
                name
              ) : isSavingsGoal ? (
                t('payment_link_v2.contribute_to_goal', {name})
              ) : amount ? (
                <Trans
                  i18nKey="payment_link_v2.pay_amount_to_name"
                  values={{amount: amount.toFixed(2), name}}
                  components={{
                    amount: <span className="payment-link-v2-header__amount" />,
                  }}
                />
              ) : (
                t('payment_link.pay', {name})
              )}
            </Typography>
          )}
          {noteContent && (
            <Typography
              component="p"
              className="payment-link-v2-header__note">
              {noteContent}
            </Typography>
          )}
        </Box>
      </Box>
    );
  },
);

PaymentLinkV2Header.displayName = 'PaymentLinkV2Header';

export default PaymentLinkV2Header;
